import { useTheme } from '../lib/theme-provider';
import { ArrowLeft, RefreshCw, FileText, User, History, BookOpen } from 'lucide-react';

interface GameHeaderProps {
  roomCode: string;
  currentRound: number;
  extensions: {
    ladyOfLake: boolean;
    excalibur: boolean;
  };
  forcedRound: string;
  publicVote: boolean;
  onBack?: () => void;
  onRefresh?: () => void;
  onNotes?: () => void;
  onIdentity?: () => void;
  onHistory?: () => void;
  onRules?: () => void;
}

export default function GameHeader({
  roomCode,
  currentRound,
  extensions,
  forcedRound,
  publicVote,
  onBack,
  onRefresh,
  onNotes,
  onIdentity,
  onHistory,
  onRules,
}: GameHeaderProps) {
  const { theme } = useTheme();

  const getExtensionLabel = () => {
    const labels = [];
    if (extensions.ladyOfLake) labels.push('有湖仙');
    if (extensions.excalibur) labels.push('有王者');
    if (forcedRound !== 'none') {
      const roundMap: Record<string, string> = {
        round3: '3轮强制',
        round5: '5轮强制',
        global3: '全局3次',
      };
      labels.push(roundMap[forcedRound]);
    }
    if (publicVote) labels.push('明票');
    return labels.join(' · ') || '标准模式';
  };

  return (
    <header
      className="flex flex-col gap-3 p-4 border-b"
      style={{
        backgroundColor: theme.colors['bg-card'],
        borderColor: theme.colors['border-primary'],
      }}
    >
      {/* 第一行：返回、房间号、快捷按钮 */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          {onBack && (
            <button
              onClick={onBack}
              className="p-2 rounded-lg transition-colors"
              style={{ backgroundColor: theme.colors['bg-primary'] }}
            >
              <ArrowLeft size={20} style={{ color: theme.colors['text-primary'] }} />
            </button>
          )}
          <div>
            <div className="flex items-center gap-2">
              <span
                className="text-xl font-bold"
                style={{ color: theme.colors['accent-gold'] }}
              >
                {roomCode}
              </span>
              <span
                className="text-xs px-2 py-0.5 rounded"
                style={{
                  backgroundColor: `${theme.colors['accent-gold']}20`,
                  color: theme.colors['accent-gold'],
                }}
              >
                第{currentRound}轮
              </span>
            </div>
            <p
              className="text-xs"
              style={{ color: theme.colors['text-secondary'] }}
            >
              {getExtensionLabel()}
            </p>
          </div>
        </div>

        {/* 快捷按钮 */}
        <div className="flex items-center gap-2">
          {onNotes && (
            <button
              onClick={onNotes}
              className="p-2 rounded-lg transition-colors"
              style={{ backgroundColor: theme.colors['bg-primary'] }}
              title="笔记"
            >
              <FileText size={18} style={{ color: theme.colors['text-secondary'] }} />
            </button>
          )}
          {onIdentity && (
            <button
              onClick={onIdentity}
              className="p-2 rounded-lg transition-colors"
              style={{ backgroundColor: theme.colors['bg-primary'] }}
              title="身份"
            >
              <User size={18} style={{ color: theme.colors['text-secondary'] }} />
            </button>
          )}
          {onHistory && (
            <button
              onClick={onHistory}
              className="p-2 rounded-lg transition-colors"
              style={{ backgroundColor: theme.colors['bg-primary'] }}
              title="历史"
            >
              <History size={18} style={{ color: theme.colors['text-secondary'] }} />
            </button>
          )}
          {onRules && (
            <button
              onClick={onRules}
              className="p-2 rounded-lg transition-colors"
              style={{ backgroundColor: theme.colors['bg-primary'] }}
              title="规则"
            >
              <BookOpen size={18} style={{ color: theme.colors['text-secondary'] }} />
            </button>
          )}
          {onRefresh && (
            <button
              onClick={onRefresh}
              className="p-2 rounded-lg transition-colors"
              style={{ backgroundColor: theme.colors['bg-primary'] }}
              title="刷新"
            >
              <RefreshCw size={18} style={{ color: theme.colors['text-secondary'] }} />
            </button>
          )}
        </div>
      </div>
    </header>
  );
}
